import { camelToSnake, snakeToCamel } from "./string";
import { CamelToSnakeKeys, SnakeToCamelKeys } from "./types";
import { LowercaseKeys, UppercaseKeys } from "./types/string";

/**
 * Creates a new object with the same values and
 * keys transformed from camelCase to snake_case.
 *
 * @typeParam T The type of the input object
 *
 * @param obj The object whose keys will be transformed
 *
 * @returns A new object with snake_case keys
 */
export function camelToSnakeKeys<T extends object>(
  obj: T,
): CamelToSnakeKeys<T> {
  const out = {} as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const outKey = typeof key === "string" ? camelToSnake(key) : key;
    out[outKey] = obj[key as keyof T];
  }
  return out as CamelToSnakeKeys<T>;
}

/**
 * Creates a new object with the same
 * values and keys transformed to lowercase.
 *
 * @typeParam T The type of the input object
 *
 * @param obj The object whose keys will be transformed
 *
 * @returns A new object with lowercase keys
 */
export function lowercaseKeys<T extends object>(obj: T): LowercaseKeys<T> {
  const out = {} as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const outKey = typeof key === "string" ? key.toLowerCase() : key;
    out[outKey] = obj[key as keyof T];
  }
  return out as LowercaseKeys<T>;
}

/**
 * Creates a new object with the same values and
 * keys transformed from snake_case to camelCase.
 *
 * @typeParam T The type of the input object
 *
 * @param obj The object whose keys will be transformed
 *
 * @returns A new object with camelCase keys
 */
export function snakeToCamelKeys<T extends object>(
  obj: T,
): SnakeToCamelKeys<T> {
  const out = {} as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const outKey = typeof key === "string" ? snakeToCamel(key) : key;
    out[outKey] = obj[key as keyof T];
  }
  return out as SnakeToCamelKeys<T>;
}

/**
 * Converts a synchronous or asynchronous
 * iterator into an asynchronous generator.
 *
 * Allows consistent handling of iterators
 * regardless of whether they are synchronous
 * or asynchronous.
 *
 * @typeParam T The type of values yielded by the iterator
 *
 * @param iterator - A sync / async iterator
 *
 * @returns An async generator
 */
export async function* toAsyncIterator<T>(
  iterator: AsyncIterator<T> | Iterator<T>,
): AsyncGenerator<T> {
  let result = await iterator.next();
  while (!result.done) {
    yield result.value;
    result = await iterator.next();
  }
}

/**
 * Creates a new object with the same
 * values and keys transformed to uppercase.
 *
 * @typeParam T The type of the input object
 *
 * @param obj The object whose keys will be transformed
 *
 * @returns A new object with uppercase keys
 */
export function uppercaseKeys<T extends object>(obj: T): UppercaseKeys<T> {
  const out = {} as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const outKey = typeof key === "string" ? key.toUpperCase() : key;
    out[outKey] = obj[key as keyof T];
  }
  return out as UppercaseKeys<T>;
}
